/**
 * Microsoft Outlook SafeLinks Unwrapper
 * 
 * Problem: Outlook wraps URLs in SafeLinks protection which corrupts auth tokens
 * Example wrapped URL:
 * https://gbr01.safelinks.protection.outlook.com/?url=https%3A%2F%2Fu56125844.ct.sendgrid.net%2Fls%2Fclick%3Fupn%3D...
 * 
 * This utility unwraps the original URL from SafeLinks wrapper
 */

/**
 * Extracts the actual URL from Microsoft Outlook SafeLinks wrapper
 * 
 * @param url - The potentially wrapped URL
 * @returns The unwrapped original URL
 */
export function unwrapSafeLinks(url: string): string {
  console.log('🔍 SafeLinks Check - Original URL:', url.substring(0, 150));
  
  // Check if it's a SafeLinks URL
  if (
    url.includes('safelinks.protection.outlook.com') ||
    url.includes('na01.safelinks.protection.outlook.com') ||
    url.includes('eur01.safelinks.protection.outlook.com') ||
    url.includes('gbr01.safelinks.protection.outlook.com') ||
    url.includes('eur02.safelinks.protection.outlook.com') ||
    url.includes('aus01.safelinks.protection.outlook.com')
  ) {
    console.log('🔗 SafeLinks wrapper detected - extracting original URL');
    
    try {
      // Parse the SafeLinks URL
      const urlObj = new URL(url);
      
      // The actual URL is in the 'url' parameter
      const actualUrl = urlObj.searchParams.get('url');
      
      if (actualUrl) {
        const decodedUrl = decodeURIComponent(actualUrl);
        console.log('✅ SafeLinks unwrapped successfully');
        console.log('📍 Unwrapped URL:', decodedUrl.substring(0, 150));
        return decodedUrl;
      } else {
        console.warn('⚠️ SafeLinks URL found but no "url" parameter');
        return url;
      }
    } catch (error) {
      console.error('❌ Error unwrapping SafeLinks URL:', error);
      return url;
    }
  }
  
  // Not a SafeLinks URL - return as is
  return url;
}

/**
 * Extracts hash parameters from a URL (accounting for SafeLinks wrapping)
 * 
 * @param url - The URL to extract hash from (may be SafeLinks wrapped)
 * @returns Object with hash string and parsed parameters
 */
export function extractHashParams(url: string): {
  hash: string;
  params: URLSearchParams;
  wasUnwrapped: boolean;
} {
  // First unwrap SafeLinks if present
  const unwrappedUrl = unwrapSafeLinks(url);
  const wasUnwrapped = unwrappedUrl !== url;
  
  if (wasUnwrapped) {
    console.log('🔓 SafeLinks unwrapped - processing original URL');
  }
  
  // Extract hash from unwrapped URL
  let hash = '';
  
  try {
    const urlObj = new URL(unwrappedUrl);
    hash = urlObj.hash.slice(1); // Remove # prefix
    
    // If there's no hash, check if tokens are in search params instead
    if (!hash && urlObj.search) {
      console.log('🔍 No hash found - checking search params for tokens');
      const searchParams = new URLSearchParams(urlObj.search);
      if (searchParams.has('access_token') || searchParams.has('token')) {
        hash = urlObj.search.slice(1); // Use search params as hash
        console.log('✅ Found tokens in search params instead of hash');
      }
    }
  } catch (error) {
    console.error('❌ Error parsing unwrapped URL:', error);
    // Fallback to simple hash extraction
    const hashIndex = unwrappedUrl.indexOf('#');
    if (hashIndex !== -1) {
      hash = unwrappedUrl.slice(hashIndex + 1);
    }
  }
  
  console.log('📋 Extracted hash:', hash.substring(0, 100));
  
  // Parse hash as URLSearchParams
  const params = new URLSearchParams(hash);
  
  return {
    hash,
    params,
    wasUnwrapped,
  };
}

/**
 * Detects if the current page load is from a SafeLinks wrapped URL
 * 
 * @returns true if SafeLinks was detected and unwrapped
 */
export function detectSafeLinks(): boolean {
  const currentUrl = window.location.href;
  const unwrapped = unwrapSafeLinks(currentUrl);
  return unwrapped !== currentUrl;
}

/**
 * Replaces the current URL with the unwrapped version (if SafeLinks detected)
 * This helps clean up the browser history
 * 
 * @param preserveHash - Whether to preserve the hash fragment
 */
export function replaceSafeLinksUrl(preserveHash: boolean = true): void {
  const currentUrl = window.location.href;
  const unwrapped = unwrapSafeLinks(currentUrl);
  
  if (unwrapped !== currentUrl) {
    console.log('🔄 Replacing SafeLinks URL in browser history');
    
    try {
      const unwrappedUrl = new URL(unwrapped);
      
      // Build the replacement URL
      let replacementUrl = `${window.location.origin}${window.location.pathname}`;
      
      if (preserveHash && unwrappedUrl.hash) {
        replacementUrl += unwrappedUrl.hash;
      }
      
      // Replace the URL without reloading
      window.history.replaceState({}, '', replacementUrl);
      console.log('✅ URL replaced successfully');
    } catch (error) {
      console.error('❌ Error replacing SafeLinks URL:', error);
    }
  }
}
